import { DEFAULT_VALUES } from '../constants';
import i18n from 'i18next';

// Track the current language
let currentLanguage = i18n.language || 'en-US';

// Update the language whenever it changes
i18n.on('languageChanged', (lng) => {
  console.log('Language changed to:', lng);
  currentLanguage = lng;
});

class ApiService {
  constructor() {
    this.baseUrl = DEFAULT_VALUES.SERVER_URL;
    this.voiceServerUrl = DEFAULT_VALUES.VOICE_SERVER_URL;
  }

  // ============================================================================
  // AUTHENTICATION
  // ============================================================================

  async login(email, password) {
    try {
      console.log('Logging in with email:', email);

      const response = await fetch(`${this.baseUrl}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': navigator.userAgent || 'SRInterviewPOC/1.0'
        },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.detail || 'Login failed');
      }

      const data = await response.json();
      // sr-unified-backend returns: { message, accessToken, user: { id, email, firstName, lastName, accessToken, ... } }
      const userData = data.user || data.data?.user || data;

      if (!userData) {
        throw new Error('Invalid response format from server');
      }

      // accessToken is at the top level and also in user object
      const accessToken = data.accessToken || userData.accessToken;

      return {
        user: userData,
        accessToken: accessToken,
        sessionId: userData.id, // Use user ID as session identifier since backend uses cookies for session
        message: data.message || 'Login successful'
      };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async register(email, password, firstName, lastName) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': navigator.userAgent || 'SRInterviewPOC/1.0'
        },
        credentials: 'include',
        body: JSON.stringify({ email, password, firstName, lastName })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.detail || 'Registration failed');
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // ============================================================================
  // JOBS
  // ============================================================================

  async getPublishedJobs(jwtToken, sessionToken, options = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (options.page) queryParams.append('page', options.page);
      if (options.limit) queryParams.append('limit', options.limit);
      queryParams.append('status', 'published');

      const queryString = queryParams.toString();
      const url = `${this.baseUrl}/api/v1/jobs${queryString ? '?' + queryString : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch jobs');
      }

      const data = await response.json();
      // Handle different response formats
      const jobs = data.data?.jobs || data.jobs || data.data || [];
      return Array.isArray(jobs) ? jobs : [];
    } catch (error) {
      console.error('Get jobs error:', error);
      throw error;
    }
  }

  async getJobById(jobId, jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/jobs/public/${jobId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch job');
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Get job error:', error);
      throw error;
    }
  }

  // ============================================================================
  // APPLICATIONS
  // ============================================================================

  async getMyApplications(jwtToken, sessionToken, options = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (options.page) queryParams.append('page', options.page);
      if (options.limit) queryParams.append('limit', options.limit);

      const queryString = queryParams.toString();
      const url = `${this.baseUrl}/api/v1/candidates/me/applications${queryString ? '?' + queryString : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch applications');
      }

      const data = await response.json();
      const applications = data.data?.applications || data.applications || data.data || [];
      return Array.isArray(applications) ? applications : [];
    } catch (error) {
      console.error('Get applications error:', error);
      throw error;
    }
  }

  async getApplicationById(applicationId, jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/candidates/me/applications/${applicationId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch application');
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Get application error:', error);
      throw error;
    }
  }

  // ============================================================================
  // INTERVIEW MANAGEMENT
  // ============================================================================

  async createInterview(jobId, applicationId, interviewRoundIndex, jwtToken, sessionToken, options = {}) {
    try {
      const requestBody = {
        jobId,
        applicationId,
        interviewRoundIndex: parseInt(interviewRoundIndex, 10),
        language: options.language || currentLanguage || 'en',
        resumable: options.resumable || false
      };

      console.log('Creating interview with:', requestBody);

      const response = await fetch(`${this.baseUrl}/api/v1/interviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Interview creation failed');
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Interview creation error:', error);
      throw error;
    }
  }

  async getInterview(sessionId, jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/interviews/${sessionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch interview');
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Get interview error:', error);
      throw error;
    }
  }

  async getInterviewsForApplication(applicationId, jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/interviews/application/${applicationId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch interviews');
      }

      const data = await response.json();
      const interviews = data.data || data;
      return Array.isArray(interviews) ? interviews : [];
    } catch (error) {
      console.error('Get interviews for application error:', error);
      throw error;
    }
  }

  async updateInterviewStatus(sessionId, status, jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/interviews/${sessionId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update interview status');
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Update interview status error:', error);
      throw error;
    }
  }

  async startInterview(sessionId, jwtToken, sessionToken) {
    return this.updateInterviewStatus(sessionId, 'active', jwtToken, sessionToken);
  }

  async completeInterview(sessionId, jwtToken, sessionToken) {
    return this.updateInterviewStatus(sessionId, 'completed', jwtToken, sessionToken);
  }

  async addInterviewMessage(sessionId, role, content, jwtToken, sessionToken, metadata = null) {
    try {
      const requestBody = { role, content };
      if (metadata) requestBody.metadata = metadata;

      const response = await fetch(`${this.baseUrl}/api/v1/interviews/${sessionId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to add message');
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Add message error:', error);
      throw error;
    }
  }

  // ============================================================================
  // VOICE BOT INITIALIZATION
  // ============================================================================

  async initializeVoiceBot(interviewId, language = 'en-US') {
    try {
      console.log('Initializing voice bot for interview:', interviewId);

      if (!interviewId) {
        throw new Error('Interview ID is required. Please create an interview first.');
      }

      const requestBody = { language };

      const response = await fetch(`${this.baseUrl}/api/v1/interviews/${interviewId}/initialize-bot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Voice bot initialization failed:', response.status, errorText);
        let error;
        try {
          error = JSON.parse(errorText);
        } catch (e) {
          error = { message: errorText };
        }
        throw new Error(error.message || `Voice bot initialization failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log('Voice bot initialized successfully');

      const responseData = data.data || data;

      return {
        sessionId: responseData.sessionId,
        roomName: responseData.roomName,
        livekitToken: responseData.livekitToken,
        livekitUrl: responseData.livekitUrl,
        expiresIn: responseData.expiresIn,
        userIdentity: responseData.userIdentity
      };
    } catch (error) {
      console.error('Voice bot initialization error:', error);
      throw error;
    }
  }

  // ============================================================================
  // HEALTH CHECK
  // ============================================================================

  async healthCheck() {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      if (!response.ok) {
        throw new Error('Health check failed');
      }
      return await response.json();
    } catch (error) {
      console.error('Health check error:', error);
      throw error;
    }
  }

  // ============================================================================
  // GENERIC HTTP METHODS
  // ============================================================================

  async post(endpoint, data, options = {}) {
    try {
      const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
      console.log('🔗 POST request to:', url);
      console.log('📦 Request data:', JSON.stringify(data, null, 2));

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error body:', errorText);
        let error;
        try {
          error = JSON.parse(errorText);
        } catch {
          error = { message: errorText || 'Request failed' };
        }
        throw new Error(error.message || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Response data:', result);
      return result;
    } catch (error) {
      console.error('POST request error:', error);
      throw error;
    }
  }

  // ============================================================================
  // LEGACY METHODS (kept for compatibility but may not work)
  // ============================================================================

  async getMyResumes(jwtToken, sessionToken, options = {}) {
    console.warn('getMyResumes is deprecated in SR version - resume data is in applications');
    return [];
  }

  async getMyJobRoles(jwtToken, sessionToken) {
    console.warn('getMyJobRoles is deprecated - use getPublishedJobs instead');
    return this.getPublishedJobs(jwtToken, sessionToken);
  }

  async getJobRoles(jwtToken, sessionToken) {
    return this.getPublishedJobs(jwtToken, sessionToken);
  }

  async getBaselineTests(jwtToken, sessionToken) {
    console.warn('getBaselineTests is not available in SR version');
    return [];
  }

  async getAvailableVoices() {
    // Return fallback voices - SR version uses voice from job interview round config
    return {
      voices: [
        { _id: 'rachel', name: 'Rachel', description: 'Clear, professional female (American)' },
        { _id: 'adam', name: 'Adam', description: 'Deep, authoritative male (American)' },
        { _id: 'domi', name: 'Domi', description: 'Strong, assertive female (American)' }
      ],
      defaultVoice: 'rachel'
    };
  }
}

export const apiService = new ApiService();
